import React from 'react';
import { motion } from 'framer-motion';
import profile from '../profile.json';
import { Profile } from '../types/profile';

const typedProfile = profile as Profile;

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const } }
};

export default function About(): React.ReactElement {
    return (
        <section id='about' className="py-24 lg:py-32">
            <div className="container-main">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    variants={fadeUp}
                    className="mb-16"
                >
                    <div className="section-label">
                        <span>About</span>
                    </div>
                    <h2 className="section-title">A bit about me</h2>
                </motion.div>

                <div className="grid lg:grid-cols-5 gap-16 items-start">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-60px" }}
                        variants={{
                            hidden: {},
                            visible: { transition: { staggerChildren: 0.1 } }
                        }}
                        className="lg:col-span-3 space-y-6"
                    >
                        <motion.p variants={fadeUp} className="text-slate-700 dark:text-dark-300 text-lg leading-relaxed">
                            {typedProfile['about-text'][0]}
                        </motion.p>

                        <motion.p variants={fadeUp} className="text-slate-700 dark:text-dark-300 text-lg leading-relaxed">
                            {typedProfile['about-text'][1]}
                            <a 
                                href='https://getmembrane.com' 
                                target='_blank' 
                                rel="noreferrer" 
                                className="text-primary-500 hover:text-primary-400 font-medium underline decoration-primary-500/30 underline-offset-4 transition-colors"
                            >
                                {typedProfile['about-text'][2]}
                            </a>
                            {typedProfile['about-text'][3]}
                        </motion.p>

                        <motion.p variants={fadeUp} className="text-slate-700 dark:text-dark-300 text-lg leading-relaxed">
                            {typedProfile['about-text'][4]}
                        </motion.p>

                        <motion.div variants={fadeUp} className="pt-8">
                            <h3 className="text-slate-900 dark:text-white font-semibold text-lg mb-6">
                                Technologies I work with
                            </h3>
                            <ul className="skill-list">
                                {typedProfile['skills'].map((skill) => (
                                    <li key={skill}>{skill}</li>
                                ))}
                            </ul>
                        </motion.div>

                        <motion.div variants={fadeUp} className="pt-8">
                            <h3 className="text-slate-900 dark:text-white font-semibold text-lg mb-6">
                                AI & LLM Tools
                            </h3>
                            <ul className="skill-list">
                                {typedProfile['ai-tools'].map((tool) => (
                                    <li key={tool}>{tool}</li>
                                ))}
                            </ul>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
                        className="lg:col-span-2 flex justify-center lg:justify-end"
                    >
                        <div className="profile-container">
                            <div className="profile-glow w-64 h-64 sm:w-72 sm:h-72 lg:w-80 lg:h-80">
                                <img 
                                    src="/gourav-raj-photo.png" 
                                    alt={`${typedProfile['first-name']} ${typedProfile['last-name']}`}
                                    className="w-full h-full object-cover rounded-[21px] grayscale-[20%] hover:grayscale-0 transition-all duration-500"
                                />
                            </div>
                            
                            <div className="absolute -bottom-4 -right-4 card px-5 py-2.5 rounded-full flex items-center gap-3">
                                <div className="pulse-ring">
                                    <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />
                                </div>
                                <span className="text-primary-500 font-mono text-sm font-medium">
                                    Available for work
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
